import { useState, useEffect } from 'preact/hooks';
import { toast } from 'sonner';
import { getCanvasScale, onCanvasScaleChange } from '../utils/uiScale';
import AvatarImage from './ui/AvatarImage';
import {
  GOLD, ACCENT_GOLD, TEXT_PRIMARY, TEXT_MUTED,
  GOLD_BTN, BEVELED_BTN, PANEL_BG, CONTENT_BG_DEFAULT,
} from '../lib/medievalTheme';
import HeraldSeal from './ui/HeraldSeal';
import { t } from '../localization/t';

// Medieval-themed friend-request toast. Sonner toasts portal to
// document.body, escaping the .app-canvas cqw container, so we read
// the canvas scale and apply pixel values directly. Same pattern
// the Toaster component itself uses.
export function FriendRequestToast({
  toastId,
  senderName,
  avatarUrl,
  onAccept,
  onDecline,
}) {
  const [scale, setScale] = useState(getCanvasScale());
  const [busy, setBusy] = useState(false);
  useEffect(() => onCanvasScaleChange(setScale), []);
  const px = (n) => Math.max(1, Math.round(n * scale));

  const run = async (fn) => {
    if (busy) return;
    setBusy(true);
    try {
      await fn();
    } finally {
      toast.dismiss(toastId);
    }
  };

  const initials = (senderName?.[0] || '?').toUpperCase();

  return (
    <div
      style={{
        // Herald card surface. Same stone-wash + gold rim recipe as
        // the achievement banner and the restyled sonner toasts, with
        // the sender's avatar stamped in a wax seal off the left edge.
        position: 'relative',
        background: `${CONTENT_BG_DEFAULT}, ${PANEL_BG}`,
        backgroundBlendMode: 'overlay, normal',
        borderRadius: `${px(8)}px`,
        padding: `${px(14)}px ${px(16)}px ${px(14)}px ${px(34)}px`,
        width: `${px(380)}px`,
        boxShadow: `0 ${px(16)}px ${px(48)}px rgba(0,0,0,0.6), 0 0 ${px(24)}px ${GOLD} 0.18), inset 0 0 0 1px ${GOLD} 0.28)`,
        color: TEXT_PRIMARY,
        opacity: busy ? 0.6 : 1,
        transition: 'opacity 120ms ease',
        isolation: 'isolate',
      }}
    >
      <HeraldSeal
        size={`${px(44)}px`}
        style={{
          position: 'absolute',
          left: `${-px(15)}px`,
          top: '50%',
          transform: 'translateY(-50%)',
        }}
      >
        {avatarUrl ? (
          <AvatarImage src={avatarUrl} className="w-full h-full" />
        ) : (
          <span style={{ color: TEXT_PRIMARY, fontSize: `${px(16)}px`, fontWeight: 700 }}>
            {initials}
          </span>
        )}
      </HeraldSeal>
      <div
        style={{
          fontSize: `${px(10)}px`,
          letterSpacing: `${Math.max(1, px(1.5))}px`,
 color: ACCENT_GOLD,
 marginBottom: `${px(10)}px`,
          fontWeight: 600,
        }}
      >
        {t('Friend Request')}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: `${px(12)}px` }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: `${px(14)}px`,
              fontWeight: 600,
              color: TEXT_PRIMARY,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {senderName}
          </div>
          <div style={{ fontSize: `${px(12)}px`, color: TEXT_MUTED, marginTop: `${px(2)}px` }}>
            {t('wants to be your friend')}
          </div>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          gap: `${px(8)}px`,
          marginTop: `${px(14)}px`,
          justifyContent: 'flex-end',
        }}
      >
        <button
          type="button"
          disabled={busy}
          onClick={() => run(onDecline)}
          style={{
            ...BEVELED_BTN,
            borderRadius: `${px(4)}px`,
            padding: `${px(6)}px ${px(14)}px`,
            fontSize: `${px(12)}px`,
            fontWeight: 600,
            color: TEXT_MUTED,
            cursor: busy ? 'not-allowed' : 'pointer',
            pointerEvents: busy ? 'none' : 'auto',
          }}
        >
          {t('Decline')}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => run(onAccept)}
          style={{
            ...GOLD_BTN,
            borderRadius: `${px(4)}px`,
            padding: `${px(6)}px ${px(14)}px`,
            fontSize: `${px(12)}px`,
            fontWeight: 700,
            cursor: busy ? 'not-allowed' : 'pointer',
            pointerEvents: busy ? 'none' : 'auto',
          }}
        >
          {t('Accept')}
        </button>
      </div>
    </div>
  );
}
